import {
	JOURNEY_DEFINITION_IDS,
	type JourneyDefinition,
	type JourneyDefinitionId,
	type ProductKey,
} from "../contracts"
import { tSprayQualifierQuestionnaireTemplate } from "./questionnaire-templates"

const tSprayQuestionnaireReference = {
	id: tSprayQualifierQuestionnaireTemplate.id,
	version: tSprayQualifierQuestionnaireTemplate.version,
} as const

export const DEFAULT_T_SPRAY_JOURNEY_DEFINITION = {
	id: "t-spray/default",
	version: 1,
	status: "active",
	productKey: "t-spray",
	workflow: {
		id: "default",
		version: 1,
	},
	questionnaire: tSprayQuestionnaireReference,
} as const satisfies JourneyDefinition

export const DEFERRED_LABS_T_SPRAY_JOURNEY_DEFINITION = {
	id: "t-spray/deferred-labs",
	version: 1,
	status: "active",
	productKey: "t-spray",
	workflow: {
		id: "deferred-labs",
		version: 1,
	},
	questionnaire: tSprayQuestionnaireReference,
} as const satisfies JourneyDefinition

export const JOURNEY_DEFINITIONS = [
	DEFAULT_T_SPRAY_JOURNEY_DEFINITION,
	DEFERRED_LABS_T_SPRAY_JOURNEY_DEFINITION,
] as const satisfies readonly JourneyDefinition[]

const journeyDefinitionIds = new Set<string>(JOURNEY_DEFINITION_IDS)
const latestJourneyDefinitionById: Readonly<Record<JourneyDefinitionId, JourneyDefinition>> = {
	[DEFAULT_T_SPRAY_JOURNEY_DEFINITION.id]: DEFAULT_T_SPRAY_JOURNEY_DEFINITION,
	[DEFERRED_LABS_T_SPRAY_JOURNEY_DEFINITION.id]: DEFERRED_LABS_T_SPRAY_JOURNEY_DEFINITION,
}
const journeyDefinitionByVersion = new Map<string, JourneyDefinition>(
	JOURNEY_DEFINITIONS.map((definition) => [
		`${definition.id}@${definition.version}`,
		definition,
	] as const),
)
const defaultJourneyDefinitionIdByProductKey: Readonly<Record<ProductKey, JourneyDefinitionId>> = {
	"t-spray": DEFAULT_T_SPRAY_JOURNEY_DEFINITION.id,
}

export function findJourneyDefinition(
	id: string,
	version?: number,
): JourneyDefinition | undefined {
	if (!journeyDefinitionIds.has(id)) {
		return undefined
	}
	const journeyDefinitionId = id as JourneyDefinitionId
	return version === undefined
		? latestJourneyDefinitionById[journeyDefinitionId]
		: journeyDefinitionByVersion.get(`${journeyDefinitionId}@${version}`)
}

export function getJourneyDefinition(id: string, version?: number): JourneyDefinition {
	const definition = findJourneyDefinition(id, version)
	if (definition === undefined) {
		throw new Error(
			version === undefined
				? `Unknown journey definition: ${id}`
				: `Unknown journey definition: ${id}@${version}`,
		)
	}
	return definition
}

export function getJourneyDefinitionForTemplateReferences(args: {
	readonly journeyDefinitionId: string
	readonly workflowTemplateId: string
	readonly workflowTemplateVersion: number
	readonly questionnaireTemplateId: string
	readonly questionnaireTemplateVersion: string
}): JourneyDefinition {
	const definition = JOURNEY_DEFINITIONS.find(
		(candidate) =>
			candidate.id === args.journeyDefinitionId &&
			candidate.workflow.id === args.workflowTemplateId &&
			candidate.workflow.version === args.workflowTemplateVersion &&
			candidate.questionnaire.id === args.questionnaireTemplateId &&
			candidate.questionnaire.version === args.questionnaireTemplateVersion,
	)
	if (definition === undefined) {
		throw new Error(`No journey definition version matches checkpoint ${args.journeyDefinitionId}`)
	}
	return definition
}

export function getJourneyDefinitionsForProductKey(
	productKey: ProductKey,
): readonly JourneyDefinition[] {
	return JOURNEY_DEFINITIONS.filter((definition) => definition.productKey === productKey)
}

export function getDefaultJourneyDefinitionForProductKey(
	productKey: ProductKey,
): JourneyDefinition {
	return getJourneyDefinition(defaultJourneyDefinitionIdByProductKey[productKey])
}
